// Section 21 (stream G): the Ledger screen's view model. Pure: (game, cfg) -> the eleven
// sections as rows and chart series, with names resolved and turns turned into seasons. The
// text (labels, the sketch's prose) is filled in by the UI from ui/text/en.ts (24.14).
import {
  catalog,
  ledgerOf,
  type AgeGroup,
  type ArrivalDecision,
  type ArrivalReason,
  type Config,
  type CrimeKind,
  type DeathCause,
  type EventId,
  type GameState,
  type ImprovementId,
  type JusticeResponse,
  type Ledger,
  type LedgerFate,
  type LedgerGroupFate,
  type LedgerTag,
  type LossReason,
  type NewGameOptions,
  type NormId,
  type Origin,
  type PersonId,
  type PolicyId,
  type RationResource,
  type SketchLine,
  type SkillId,
} from '../sim/api';
import { stampOfTurn, type SeasonStamp } from './calendar';

export type LedgerSectionId =
  | 'outcome'
  | 'people'
  | 'wentWithout'
  | 'chosen'
  | 'norms'
  | 'justice'
  | 'gate'
  | 'inequality'
  | 'knowledge'
  | 'fragility'
  | 'sketch';

/** Section 21's eleven sections, in its order. */
export const LEDGER_SECTIONS: readonly LedgerSectionId[] = [
  'outcome',
  'people',
  'wentWithout',
  'chosen',
  'norms',
  'justice',
  'gate',
  'inequality',
  'knowledge',
  'fragility',
  'sketch',
];

export interface ChartPoint {
  /** The season (turn) the value was recorded at. */
  x: number;
  y: number;
}

export interface ChartMark {
  x: number;
  count: number;
  eventIds: EventId[];
  at: SeasonStamp;
}

export interface LedgerVM {
  seasonsPerYear: number;
  outcome: {
    kind: 'won' | 'lost' | 'ongoing';
    reason: LossReason | null;
    at: SeasonStamp | null;
    seed: string;
    seasons: number;
    options: NewGameOptions;
    ironman: boolean;
    sandbox: boolean;
    counter: number;
    target: number;
    /** Section 20: offered after a win, once. */
    canContinueSandbox: boolean;
  };
  people: {
    births: number;
    deaths: number;
    deathsByCause: { cause: DeathCause; count: number }[];
    peak: number;
    final: number;
    lifeExpectancyYears: { origin: Origin; years: number | null; atDeathYears: number | null }[];
    oldest: { name: string; years: number } | null;
    youngestDeath: { name: string; years: number; cause: DeathCause; at: SeasonStamp } | null;
    deprivationDeaths: number;
  };
  wentWithout: {
    rows: {
      personId: PersonId;
      name: string;
      group: AgeGroup;
      origin: Origin;
      seasonsShort: number;
      seasonsNone: number;
      fate: LedgerFate;
    }[];
    /** Per group, in Section 21's summary-line order: children, adults, elders. */
    groups: { group: AgeGroup; percent: number; personSeasons: number }[];
  };
  chosen: {
    at: SeasonStamp;
    resource: RationResource;
    shortfall: number;
    pinnedNone: string[];
    byHand: boolean;
  }[];
  norms: {
    rows: { at: SeasonStamp; normId: NormId; change: 'established' | 'lost'; announced: boolean }[];
    policies: { at: SeasonStamp; policyId: PolicyId; label: string; value: string }[];
  };
  justice: {
    rows: {
      at: SeasonStamp;
      crime: CrimeKind;
      offender: string;
      response: JusticeResponse | null;
      reoffended: boolean;
      fate: LedgerFate;
      afterSeasons: number | null;
    }[];
    corporalPunishments: number;
    executions: number;
    exiles: number;
  };
  gate: {
    rows: {
      at: SeasonStamp;
      size: number;
      reason: ArrivalReason;
      decision: ArrivalDecision;
      admitted: number;
      starvingFamily: boolean;
    }[];
    admittedPercent: number;
    turnedAwayPercent: number;
    groups: { origin: Origin; fate: LedgerGroupFate; lifeExpectancyYears: number | null }[];
    /** Reason is an EmigrationReason id (Section 14). */
    departures: { at: SeasonStamp; names: string[]; reason: string }[];
  };
  inequality: {
    series: ChartPoint[];
    peak: number;
    peakAt: SeasonStamp | null;
    mean: number;
  };
  knowledge: {
    literacy: ChartPoint[];
    finalLiteracyPercent: number;
    skillsLost: { at: SeasonStamp; skill: SkillId; name: string; level: number }[];
    improvements: { at: SeasonStamp; improvementId: ImprovementId }[];
  };
  fragility: {
    series: ChartPoint[];
    marks: ChartMark[];
    peak: number;
    peakAt: SeasonStamp | null;
    mean: number;
  };
  tags: LedgerTag[];
  sketch: SketchLine[];
  score: Ledger['score'];
}

const years = (seasons: number, perYear: number): number => Math.floor(seasons / perYear);

function series(values: readonly number[], scale = 1): ChartPoint[] {
  return values.map((y, i) => ({ x: i + 1, y: y * scale }));
}

/** Build the Ledger view model; `cfg` is the run's config (a save's snapshot), if not current. */
export function ledgerVM(game: GameState, cfg?: Config): LedgerVM {
  const l = ledgerOf(game, cfg);
  const perYear = catalog().seasonsPerYear;
  const name = (id: PersonId): string => l.names[id] ?? id;
  const o = l.outcome.outcome;
  const origins: Origin[] = ['founder', 'born', 'newcomer'];
  return {
    seasonsPerYear: perYear,
    outcome: {
      kind: o === null ? 'ongoing' : o.kind,
      reason: o !== null && o.kind === 'lost' ? o.reason : null,
      at: o === null ? null : stampOfTurn(o.turn),
      seed: l.outcome.seed,
      seasons: l.outcome.seasons,
      options: l.outcome.options,
      ironman: l.outcome.ironman,
      sandbox: l.outcome.sandbox,
      counter: l.outcome.steadyCounter,
      target: l.outcome.steadyTarget,
      canContinueSandbox: o !== null && o.kind === 'won' && !l.outcome.sandbox,
    },
    people: {
      births: l.people.births,
      deaths: Object.values(l.people.deathsByCause).reduce((a, n) => a + n, 0),
      deathsByCause: (Object.entries(l.people.deathsByCause) as [DeathCause, number][])
        .map(([cause, count]) => ({ cause, count }))
        .sort((a, b) => b.count - a.count || (a.cause < b.cause ? -1 : 1)),
      peak: l.people.peakPopulation,
      final: l.people.finalPopulation,
      lifeExpectancyYears: origins.map((origin) => {
        const le = l.people.lifeExpectancySeasons[origin];
        const atDeath = l.people.meanAgeAtDeathSeasons[origin];
        return {
          origin,
          years: le === undefined ? null : le / perYear,
          atDeathYears: atDeath === undefined ? null : atDeath / perYear,
        };
      }),
      oldest:
        l.people.oldest === null
          ? null
          : { name: l.people.oldest.name, years: years(l.people.oldest.ageSeasons, perYear) },
      youngestDeath:
        l.people.youngestDeath === null
          ? null
          : {
              name: l.people.youngestDeath.name,
              years: years(l.people.youngestDeath.ageSeasons, perYear),
              cause: l.people.youngestDeath.cause,
              at: stampOfTurn(l.people.youngestDeath.turn),
            },
      deprivationDeaths: l.people.deprivationDeaths,
    },
    wentWithout: {
      rows: l.wentWithout.records.map((r) => ({
        personId: r.personId,
        name: r.name,
        group: r.ageGroup,
        origin: r.origin,
        seasonsShort: r.seasonsShort,
        seasonsNone: r.seasonsFedNone,
        fate: r.fate,
      })),
      groups: (['child', 'adult', 'elder'] as const).map((group) => ({
        group,
        percent: Math.round(l.wentWithout.shortShareByGroup[group] * 100),
        personSeasons: l.wentWithout.personSeasons[group].total,
      })),
    },
    chosen: l.chosen.map((c) => ({
      at: stampOfTurn(c.turn),
      resource: c.resource,
      shortfall: c.shortfall,
      pinnedNone: c.pinnedNone.map(name),
      byHand: c.byHand,
    })),
    norms: {
      rows: l.norms.map((n) => ({
        at: stampOfTurn(n.turn),
        normId: n.normId,
        change: n.change,
        announced: n.announced,
      })),
      policies: l.policyChanges.map((p) => ({
        at: stampOfTurn(p.turn),
        policyId: p.policyId,
        label: catalog().policies.find((x) => x.id === p.policyId)?.label ?? p.policyId,
        value: p.value,
      })),
    },
    justice: {
      rows: l.justice.caseRows.map((c) => ({
        at: stampOfTurn(c.turn),
        crime: c.crime,
        offender: c.offenderName,
        response: c.response,
        reoffended: c.reoffended,
        fate: c.fate,
        afterSeasons: c.fateAfterSeasons,
      })),
      corporalPunishments: l.justice.corporalPunishments,
      executions: l.justice.executions,
      exiles: l.justice.exiles,
    },
    gate: {
      rows: l.gate.arrivals.map((a) => ({
        at: stampOfTurn(a.turn),
        size: a.size,
        reason: a.reason,
        decision: a.decision,
        admitted: a.admittedIds.length,
        starvingFamily: a.starvingFamily,
      })),
      admittedPercent: Math.round(l.gate.admittedShare * 100),
      turnedAwayPercent: Math.round(l.gate.turnedAwayShare * 100),
      groups: (
        [
          ['founder', l.gate.founders],
          ['born', l.gate.born],
          ['newcomer', l.gate.newcomers],
        ] as const
      ).map(([origin, fate]) => ({
        origin,
        fate,
        lifeExpectancyYears:
          fate.lifeExpectancySeasons === null ? null : fate.lifeExpectancySeasons / perYear,
      })),
      departures: l.gate.departures.map((d) => ({
        at: stampOfTurn(d.turn),
        names: d.personIds.map(name),
        reason: d.reason,
      })),
    },
    inequality: {
      series: series(l.inequality.series),
      peak: l.inequality.peak,
      peakAt: l.inequality.peakTurn === 0 ? null : stampOfTurn(l.inequality.peakTurn),
      mean: l.inequality.mean,
    },
    knowledge: {
      literacy: series(l.knowledge.literacySeries, 100),
      finalLiteracyPercent: Math.round(l.knowledge.finalLiteracy * 100),
      skillsLost: l.knowledge.skillsLost.map((s) => ({
        at: stampOfTurn(s.turn),
        skill: s.skill,
        name: name(s.personId),
        level: s.level,
      })),
      improvements: l.knowledge.improvements.map((i) => ({
        at: stampOfTurn(i.turn),
        improvementId: i.improvementId,
      })),
    },
    fragility: {
      series: series(l.fragility.series),
      marks: l.fragility.marks.map((m) => ({
        x: m.turn,
        count: m.count,
        eventIds: m.eventIds,
        at: stampOfTurn(m.turn),
      })),
      peak: l.fragility.peak,
      peakAt: l.fragility.peakTurn === 0 ? null : stampOfTurn(l.fragility.peakTurn),
      mean: l.fragility.mean,
    },
    tags: l.tags,
    sketch: l.sketch,
    score: l.score,
  };
}
